import * as React from "react"
import { Dialog, Box, Text, chakra } from "@chakra-ui/react"
import { useLeadForm } from "../context/LeadFormContext"
import { trackLead } from "../utils/trackLead"
import { trackEvent } from "../utils/trackEvent"

/* Shared input styles */
const inputStyles = {
  padding: "11px 14px",
  borderRadius: "10px",
  border: "1px solid #E2E6F2",
  bg: "#FAFBFE",
  fontFamily: "inherit",
  fontSize: "0.9rem",
  color: "#1E2740",
  outline: "none",
  w: "100%" as const,
  transition: "border-color 0.2s, box-shadow 0.2s",
  _focus: {
    borderColor: "#6791CB",
    boxShadow: "0 0 0 3px rgba(103,145,203,0.08)",
  },
  _placeholder: { color: "#8B95B0" },
}

const labelStyles = {
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "#1E2740",
  letterSpacing: "0.2px",
}

const LeadFormModal: React.FC = () => {
  const { isOpen, close } = useLeadForm()
  const [showSuccess, setShowSuccess] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState("")
  const formRef = React.useRef<HTMLFormElement>(null)
  const autoCloseTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Top-of-funnel: fire once each time the modal opens (drop-off denominator).
  React.useEffect(() => {
    if (isOpen) trackEvent("form_start", { form_id: "pm_modal" })
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError("")
    setSubmitting(true)

    const form = e.currentTarget
    const fd = new FormData(form)
    const data: Record<string, string | string[]> = {}
    fd.forEach((value, key) => {
      if (key === "services") {
        if (!Array.isArray(data.services)) data.services = []
        ;(data.services as string[]).push(value as string)
      } else {
        data[key] = value as string
      }
    })

    data.formType = "propertyManager"

    // Console log as fallback/debug per D-137 (dev only — PII safety)
    if (process.env.NODE_ENV !== "production") {
      console.log("Spot PM Request:", data)
    }

    // Submit to Cloudflare Pages Function at /api/submit-lead (same-origin)
    try {
      const res = await fetch("/api/submit-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      const json = (await res.json()) as { result?: string; error?: string }
      if (json.result !== "success") {
        throw new Error(json.error ?? "Submit failed")
      }
    } catch (err) {
      console.error("Submit failed:", err)
      setError("Something went wrong. Please try again.")
      setSubmitting(false)
      return
    }

    // Lead confirmed saved — fire Meta Pixel Lead event + GA4 conversion.
    trackLead("propertyManager")
    trackEvent("generate_lead", {
      form_id: "pm_modal",
      lead_type: "property_manager",
      services: Array.isArray(data.services) ? data.services : [],
    })

    setSubmitting(false)
    setShowSuccess(true)

    // Auto-close after 3.5s per D-140
    autoCloseTimer.current = setTimeout(() => {
      close()
      // Reset form 400ms after close per D-140
      setTimeout(() => {
        setShowSuccess(false)
        setError("")
        formRef.current?.reset()
      }, 400)
    }, 3500)
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details: { open: boolean }) => {
        if (!details.open && !showSuccess) {
          // Clear auto-close timer if user closes manually
          if (autoCloseTimer.current) {
            clearTimeout(autoCloseTimer.current)
            autoCloseTimer.current = null
          }
          close()
          // Reset form after close animation
          setTimeout(() => {
            setError("")
            formRef.current?.reset()
          }, 400)
        }
      }}
      placement="center"
      closeOnInteractOutside={true}
    >
      <Dialog.Backdrop
        bg="rgba(15,22,41,0.55)"
        backdropFilter="blur(6px)"
      />
      <Dialog.Positioner>
        <Dialog.Content
          bg="#FFFFFF"
          maxW="520px"
          w="100%"
          p={{ base: "32px 24px", sm: "44px 40px" }}
          borderRadius="16px"
          boxShadow="0 32px 80px rgba(15,22,41,0.18)"
          maxH="90vh"
          overflowY="auto"
          position="relative"
        >
          {/* Custom close button per D-130 */}
          <Dialog.CloseTrigger
            position="absolute"
            top="18px"
            right="18px"
            w="32px"
            h="32px"
            borderRadius="50%"
            bg="#F3F5FB"
            border="1px solid #E2E6F2"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            fontSize="1rem"
            color="#8B95B0"
            transition="background 0.2s, color 0.2s"
            _hover={{ bg: "#E2E6F2", color: "#1E2740" }}
            aria-label="Close"
          >
            {"\u2715"}
          </Dialog.CloseTrigger>

          {/* Form view -- visible when !showSuccess */}
          {!showSuccess && (
            <Box>
              <Text
                as="h2"
                fontSize="1.5rem"
                fontWeight={800}
                color="#1E2740"
                letterSpacing="-0.5px"
                mb="6px"
              >
                Get Started with Spot
              </Text>
              <Text fontSize="0.9rem" color="#8B95B0" mb="28px">
                Tell us about your business and we&apos;ll be in touch within 24
                hours.
              </Text>
              <form ref={formRef} onSubmit={handleSubmit}>
                <Box
                  display="grid"
                  gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }}
                  gap="16px"
                >
                  {/* First Name */}
                  <Box display="flex" flexDirection="column" gap="6px">
                    <chakra.label htmlFor="firstName" {...labelStyles}>First Name</chakra.label>
                    <chakra.input
                      id="firstName"
                      type="text"
                      name="firstName"
                      placeholder="Jane"
                      required
                      {...inputStyles}
                    />
                  </Box>

                  {/* Last Name */}
                  <Box display="flex" flexDirection="column" gap="6px">
                    <chakra.label htmlFor="lastName" {...labelStyles}>Last Name</chakra.label>
                    <chakra.input
                      id="lastName"
                      type="text"
                      name="lastName"
                      placeholder="Smith"
                      required
                      {...inputStyles}
                    />
                  </Box>

                  {/* Email */}
                  <Box display="flex" flexDirection="column" gap="6px">
                    <chakra.label htmlFor="email" {...labelStyles}>Email</chakra.label>
                    <chakra.input
                      id="email"
                      type="email"
                      name="email"
                      placeholder="jane@email.com"
                      required
                      {...inputStyles}
                    />
                  </Box>

                  {/* Phone */}
                  <Box display="flex" flexDirection="column" gap="6px">
                    <chakra.label htmlFor="phone" {...labelStyles}>Phone</chakra.label>
                    <chakra.input
                      id="phone"
                      type="tel"
                      name="phone"
                      placeholder="(555) 123-4567"
                      {...inputStyles}
                    />
                  </Box>

                  {/* Property Management Company */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap="6px"
                    gridColumn="1/-1"
                  >
                    <chakra.label htmlFor="pmCompany" {...labelStyles}>
                      Property Management Company
                    </chakra.label>
                    <chakra.input
                      id="pmCompany"
                      type="text"
                      name="pmCompany"
                      placeholder="Your PM company name"
                      required
                      {...inputStyles}
                    />
                  </Box>

                  {/* Door Count */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap="6px"
                    gridColumn="1/-1"
                  >
                    <chakra.label htmlFor="doorCount" {...labelStyles}>Door Count</chakra.label>
                    <chakra.input
                      id="doorCount"
                      type="text"
                      name="doorCount"
                      placeholder="e.g. 250"
            inputMode="numeric"
                      required
                      {...inputStyles}
                    />
                  </Box>

                  {/* Checkbox group per D-134 */}
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap="6px"
                    gridColumn="1/-1"
                  >
                    <chakra.label {...labelStyles}>
                      Which service(s) are you interested in?
                    </chakra.label>
                    <Box display="flex" flexDirection="column" gap="12px">
                      {([
                        ["Guaranteed Rent", "guaranteed-rent"],
                        ["Rent Advance", "rent-advance"],
                        ["Property Accounting", "property-accounting"],
                      ] as [string, string][]).map(([label, value]) => (
                        <chakra.label
                          key={label}
                          display="flex"
                          alignItems="center"
                          gap="10px"
                          fontSize="0.9rem"
                          color="#1E2740"
                          cursor="pointer"
                          fontWeight={500}
                        >
                          <chakra.input
                            type="checkbox"
                            name="services"
                            value={value}
                            width="18px"
                            height="18px"
                            borderRadius="5px"
                            border="1.5px solid #E2E6F2"
                            bg="#FAFBFE"
                            accentColor="#6791CB"
                            cursor="pointer"
                            flexShrink={0}
                          />
                          {label}
                        </chakra.label>
                      ))}
                    </Box>
                  </Box>

                  {/* SMS consent — TCPA/carrier compliance; opt-in, not required to submit */}
                  <Box gridColumn="1/-1">
                    <chakra.label
                      display="flex"
                      alignItems="flex-start"
                      gap="10px"
                      fontSize="0.78rem"
                      color="#4A5578"
                      lineHeight="1.55"
                      cursor="pointer"
                    >
                      <chakra.input
                        type="checkbox"
                        name="smsConsent"
                        value="yes"
                        width="18px"
                        height="18px"
                        borderRadius="5px"
                        border="1.5px solid #E2E6F2"
                        bg="#FAFBFE"
                        accentColor="#6791CB"
                        cursor="pointer"
                        flexShrink={0}
                        mt="2px"
                      />
                      <chakra.span>
                        I agree to receive transactional SMS messages from Spot
                        at the phone number provided, including account updates,
                        coverage status, and support replies. Msg frequency
                        varies. Msg &amp; data rates may apply. Reply STOP to
                        opt out. Consent is not a condition of purchase. See
                        our{" "}
                        <chakra.a
                          href="/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          color="#6791CB"
                          textDecoration="underline"
                        >
                          Privacy Policy
                        </chakra.a>{" "}
                        and{" "}
                        <chakra.a
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          color="#6791CB"
                          textDecoration="underline"
                        >
                          Terms of Service
                        </chakra.a>
                        .
                      </chakra.span>
                    </chakra.label>
                  </Box>
                </Box>

                {/* Submit button per source line 512-525 */}
                <chakra.button
                  type="submit"
                  mt="24px"
                  w="100%"
                  padding="14px"
                  borderRadius="50px"
                  bg="#6791CB"
                  color="#FFFFFF"
                  fontFamily="inherit"
                  fontSize="0.95rem"
                  fontWeight={700}
                  border="none"
                  cursor="pointer"
                  boxShadow="0 4px 20px rgba(103,145,203,0.25)"
                  transition="background 0.3s, box-shadow 0.3s, transform 0.3s"
                  _hover={{
                    bg: "#7BA3D6",
                    boxShadow: "0 8px 28px rgba(103,145,203,0.35)",
                    transform: "translateY(-1px)",
                  }}
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </chakra.button>

                {/* Error message */}
                {error && (
                  <Text
                    fontSize="0.82rem"
                    color="#E8943A"
                    textAlign="center"
                    mt="8px"
                  >
                    {error}
                  </Text>
                )}

                {/* Form footer */}
                <Text
                  textAlign="center"
                  mt="16px"
                  fontSize="0.75rem"
                  color="#8B95B0"
                >
                  We&apos;ll never share your information.
                </Text>
              </form>
            </Box>
          )}

          {/* Success view -- visible when showSuccess */}
          {showSuccess && (
            <Box textAlign="center" py="20px">
              <Box
                w="56px"
                h="56px"
                borderRadius="50%"
                bg="rgba(16,185,129,0.08)"
                mx="auto"
                mb="16px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="1.5rem"
                color="#10B981"
              >
                {"\u2713"}
              </Box>
              <Text
                as="h3"
                fontSize="1.2rem"
                fontWeight={700}
                color="#1E2740"
                mb="8px"
              >
                Request Received!
              </Text>
              <Text fontSize="0.9rem" color="#4A5578">
                Our team will review your information and reach out within 24
                hours to walk you through next steps.
              </Text>
            </Box>
          )}
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  )
}

export default LeadFormModal
